import { useEffect, useRef, useState } from 'react';
import CountUp from '../components/CountUp.jsx';

function useInViewOnce(options = { threshold: 0.2 }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      });
    }, options);
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView, options]);
  return [ref, inView];
}

function Section({ variant, children }) {
  return (
    <section className={`cs-section--${variant || 'default'}`}>
      <div className="container">{children}</div>
    </section>
  );
}

export function StatStrip({ items, period }) {
  return (
    <Section variant="stat-strip">
      <div className="cs-stat-strip">
        <div className="cs-stat-strip__grid">
          {items.map((it, i) => (
            <div key={i} className={`cs-stat-strip__cell${it.highlight ? ' is-highlight' : ''}`}>
              <CountUp value={it.num} className="cs-stat-strip__num" />
              <span className="cs-stat-strip__label">{it.label}</span>
            </div>
          ))}
        </div>
        {period && <span className="cs-stat-strip__period">{period}</span>}
      </div>
    </Section>
  );
}

export function Friction({ question, oldWay, newWay, callout }) {
  return (
    <Section variant="friction">
      <div className="cs-friction">
        <h3 className="cs-friction__q">{question}</h3>
        <div className="cs-friction__pair">
          <div className="cs-friction__col cs-friction__col--old">
            <span className="cs-friction__tag">The old way</span>
            <p>{oldWay}</p>
          </div>
          <div className="cs-friction__col cs-friction__col--new">
            <span className="cs-friction__tag">With Account Editor</span>
            <p>{newWay}</p>
          </div>
        </div>
        {callout && <p className="cs-friction__callout" dangerouslySetInnerHTML={{ __html: callout }} />}
      </div>
    </Section>
  );
}

export function HeroStat({ num, label, period, breakdown }) {
  return (
    <Section variant="hero-stat">
      <div className="cs-hero-stat">
        <div className="cs-hero-stat__top">
          <CountUp value={num} duration={1600} className="cs-hero-stat__num" />
          <div className="cs-hero-stat__meta">
            <span>{label}</span>
            {period && <span className="cs-hero-stat__period">{period}</span>}
          </div>
        </div>
        {breakdown && breakdown.length > 0 && (
          <div className="cs-hero-stat__breakdown">
            {breakdown.map((b, i) => (
              <div key={i} className="cs-hero-stat__cell">
                <span className="cs-hero-stat__cell-label">{b.label}</span>
                <CountUp value={b.num} className="cs-hero-stat__cell-num" />
                {b.note && <span className="cs-hero-stat__cell-note">{b.note}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

export function StackedBar({ title, total, totalLabel = 'total actions', segments }) {
  const sum = segments.reduce((acc, s) => acc + s.value, 0);
  const max = Math.max(...segments.map((s) => s.value));
  const [ref, inView] = useInViewOnce();
  return (
    <Section variant="bar">
      <div className="cs-bar">
        <div className="cs-bar__head">
          <span className="cs-bar__title">{title}</span>
          <span className="cs-bar__total">
            {totalLabel} <strong>{total}</strong>
          </span>
        </div>
        <div ref={ref} className={`cs-bar__chart${inView ? ' is-in' : ''}`}>
          {segments.map((s, i) => {
            const pct = Math.round((s.value / sum) * 100);
            return (
              <div key={i} className="cs-bar__row">
                <div className="cs-bar__row-head">
                  <span className="cs-bar__row-label">
                    <span
                      className={`cs-bar__leg-dot cs-bar__leg-dot--${s.tone || 'a'}`}
                      aria-hidden="true"
                    />
                    {s.label}
                  </span>
                  <span className="cs-bar__row-val">
                    <strong>{s.value.toLocaleString()}</strong>
                    <span> · {pct}%</span>
                  </span>
                </div>
                <div
                  className="cs-bar__track"
                  role="img"
                  aria-label={`${s.label}: ${s.value.toLocaleString()} (${pct}% of ${total})`}
                >
                  <span
                    className={`cs-bar__fill cs-bar__seg--${s.tone || 'a'}`}
                    style={{ '--w': `${(s.value / max) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

export function Callout({ children }) {
  return (
    <Section variant="callout">
      <p className="cs-callout">{children}</p>
    </Section>
  );
}

export function Shot({ src, alt, caption, eyebrow = 'In the product', width = 1600, height = 747 }) {
  return (
    <Section variant="shot">
      <figure className={`cs-shot${caption ? ' cs-shot--split' : ''}`}>
        <div className="cs-shot__frame">
          <img src={src} alt={alt} loading="lazy" decoding="async" width={width} height={height} />
        </div>
        {caption && (
          <figcaption className="cs-shot__text">
            {eyebrow && <span className="cs-shot__eyebrow">{eyebrow}</span>}
            <span className="cs-shot__caption">{caption}</span>
          </figcaption>
        )}
      </figure>
    </Section>
  );
}

export function Takeaway({ children, footnote }) {
  return (
    <Section variant="takeaway">
      <div className="cs-takeaway">
        <span className="cs-takeaway__eyebrow">The bottom line</span>
        <p className="cs-takeaway__body">{children}</p>
        {footnote && <span className="cs-takeaway__footnote">{footnote}</span>}
      </div>
    </Section>
  );
}

export function CategoryGrid({ groups }) {
  return (
    <Section variant="cats">
      <div className="cs-cats">
        {groups.map((g, i) => (
          <div key={i} className="cs-cats__col">
            <span className="cs-cats__title">{g.title}</span>
            <ul className="cs-cats__list">
              {g.items.map((it, j) => (
                <li key={j}>
                  <span className="cs-cats__label">{it.label}</span>
                  <strong>{it.num}</strong>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function Intro({ figure, figureLabel, children }) {
  return (
    <Section variant="intro">
      <div className={`cs-intro${figure ? '' : ' cs-intro--text-only'}`}>
        {figure && (
          <div className="cs-intro__figure">
            <CountUp value={figure} duration={1800} className="cs-intro__num" />
            {figureLabel && <span className="cs-intro__num-label">{figureLabel}</span>}
          </div>
        )}
        <p className="cs-intro__text">{children}</p>
      </div>
    </Section>
  );
}

export function SectionLead({ eyebrow, title, children }) {
  return (
    <Section variant="lead">
      <div className="cs-lead">
        {eyebrow && <span className="cs-lead__eyebrow">{eyebrow}</span>}
        <h2 className="cs-lead__title">{title}</h2>
        {children && <p className="cs-lead__body">{children}</p>}
      </div>
    </Section>
  );
}

export function Disclaimer({ children }) {
  return (
    <Section variant="disclaimer">
      <p className="cs-disclaimer">{children}</p>
    </Section>
  );
}
